import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Initial state
const initialState = {
  // KPI data
  kpis: {
    total_orders: 0,
    total_value: 0,
    avg_order_value: 0,
    pending_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    month_over_month_growth: 0,
  },

  // Status breakdown
  statusBreakdown: [],

  // Top customers
  topCustomers: [],

  // Monthly trends
  monthlyTrends: [],

  // Amount distribution
  amountDistribution: [],

  // Order type breakdown
  orderTypeBreakdown: [],

  // Season performance
  seasonPerformance: [],

  // Sales by sales person (division)
  salesBySalesPerson: [],

  // Sales by currency (forex exposure)
  salesByCurrency: [],

  // Loading states
  isLoadingKpis: false,
  isLoadingStatusBreakdown: false,
  isLoadingTopCustomers: false,
  isLoadingMonthlyTrends: false,
  isLoadingAmountDistribution: false,
  isLoadingOrderTypeBreakdown: false,
  isLoadingSeasonPerformance: false,
  isLoadingSalesBySalesPerson: false,
  isLoadingSalesByCurrency: false,

  // Error states
  kpisError: null,
  statusBreakdownError: null,
  topCustomersError: null,
  monthlyTrendsError: null,
  amountDistributionError: null,
  orderTypeBreakdownError: null,
  seasonPerformanceError: null,
  salesBySalesPersonError: null,
  salesByCurrencyError: null,

  // Last updated timestamp
  lastUpdated: null,
};

// Async thunks for fetching analytics data

/**
 * Fetch KPI summary
 */
export const fetchSalesKPIs = createAsyncThunk(
  'salesAnalytics/fetchKPIs',
  async ({ dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/crm/api/sales-orders/analytics/kpis/', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch KPIs'
      );
    }
  }
);

/**
 * Fetch status breakdown
 */
export const fetchStatusBreakdown = createAsyncThunk(
  'salesAnalytics/fetchStatusBreakdown',
  async ({ dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/crm/api/sales-orders/analytics/status-breakdown/', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch status breakdown'
      );
    }
  }
);

/**
 * Fetch top customers
 */
export const fetchTopCustomers = createAsyncThunk(
  'salesAnalytics/fetchTopCustomers',
  async ({ limit = 10, dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = { limit };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/crm/api/sales-orders/analytics/top-customers/', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch top customers'
      );
    }
  }
);

/**
 * Fetch monthly trends
 */
export const fetchMonthlyTrends = createAsyncThunk(
  'salesAnalytics/fetchMonthlyTrends',
  async ({ months = 12 } = {}, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/api/sales-orders/analytics/monthly-trends/', {
        params: { months }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch monthly trends'
      );
    }
  }
);

/**
 * Fetch amount distribution
 */
export const fetchAmountDistribution = createAsyncThunk(
  'salesAnalytics/fetchAmountDistribution',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/api/sales-orders/analytics/amount-distribution/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch amount distribution'
      );
    }
  }
);

/**
 * Fetch order type breakdown
 */
export const fetchOrderTypeBreakdown = createAsyncThunk(
  'salesAnalytics/fetchOrderTypeBreakdown',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/api/sales-orders/analytics/order-type-breakdown/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch order type breakdown'
      );
    }
  }
);

/**
 * Fetch season performance
 */
export const fetchSeasonPerformance = createAsyncThunk(
  'salesAnalytics/fetchSeasonPerformance',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/crm/api/sales-orders/analytics/season-performance/');
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch season performance'
      );
    }
  }
);

/**
 * Fetch sales by sales person (division)
 */
export const fetchSalesBySalesPerson = createAsyncThunk(
  'salesAnalytics/fetchSalesBySalesPerson',
  async ({ dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/crm/api/sales-orders/analytics/sales-by-sales-person/', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch sales by sales person'
      );
    }
  }
);

/**
 * Fetch sales by currency (forex exposure)
 */
export const fetchSalesByCurrency = createAsyncThunk(
  'salesAnalytics/fetchSalesByCurrency',
  async ({ dateFrom, dateTo } = {}, { rejectWithValue }) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const response = await api.get('/crm/api/sales-orders/analytics/sales-by-currency/', { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch sales by currency'
      );
    }
  }
);

/**
 * Fetch all analytics data at once
 */
export const fetchAllAnalytics = createAsyncThunk(
  'salesAnalytics/fetchAll',
  async ({ dateFrom, dateTo } = {}, { dispatch }) => {
    const dateParams = { dateFrom, dateTo };

    // Dispatch all analytics requests in parallel
    await Promise.all([
      dispatch(fetchSalesKPIs(dateParams)),
      dispatch(fetchStatusBreakdown(dateParams)),
      dispatch(fetchTopCustomers({ ...dateParams, limit: 10 })),
      dispatch(fetchMonthlyTrends({ months: 12 })),
      dispatch(fetchAmountDistribution()),
      dispatch(fetchOrderTypeBreakdown()),
      dispatch(fetchSeasonPerformance()),
      dispatch(fetchSalesBySalesPerson(dateParams)),
      dispatch(fetchSalesByCurrency(dateParams)),
    ]);
  }
);

// Slice
const salesAnalyticsSlice = createSlice({
  name: 'salesAnalytics',
  initialState,
  reducers: {
    clearAnalyticsErrors: (state) => {
      state.kpisError = null;
      state.statusBreakdownError = null;
      state.topCustomersError = null;
      state.monthlyTrendsError = null;
      state.amountDistributionError = null;
      state.orderTypeBreakdownError = null;
      state.seasonPerformanceError = null;
      state.salesBySalesPersonError = null;
      state.salesByCurrencyError = null;
    },
    resetAnalytics: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch KPIs
      .addCase(fetchSalesKPIs.pending, (state) => {
        state.isLoadingKpis = true;
        state.kpisError = null;
      })
      .addCase(fetchSalesKPIs.fulfilled, (state, action) => {
        state.isLoadingKpis = false;
        state.kpis = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSalesKPIs.rejected, (state, action) => {
        state.isLoadingKpis = false;
        state.kpisError = action.payload;
      })

      // Fetch Status Breakdown
      .addCase(fetchStatusBreakdown.pending, (state) => {
        state.isLoadingStatusBreakdown = true;
        state.statusBreakdownError = null;
      })
      .addCase(fetchStatusBreakdown.fulfilled, (state, action) => {
        state.isLoadingStatusBreakdown = false;
        state.statusBreakdown = action.payload;
      })
      .addCase(fetchStatusBreakdown.rejected, (state, action) => {
        state.isLoadingStatusBreakdown = false;
        state.statusBreakdownError = action.payload;
      })

      // Fetch Top Customers
      .addCase(fetchTopCustomers.pending, (state) => {
        state.isLoadingTopCustomers = true;
        state.topCustomersError = null;
      })
      .addCase(fetchTopCustomers.fulfilled, (state, action) => {
        state.isLoadingTopCustomers = false;
        state.topCustomers = action.payload;
      })
      .addCase(fetchTopCustomers.rejected, (state, action) => {
        state.isLoadingTopCustomers = false;
        state.topCustomersError = action.payload;
      })

      // Fetch Monthly Trends
      .addCase(fetchMonthlyTrends.pending, (state) => {
        state.isLoadingMonthlyTrends = true;
        state.monthlyTrendsError = null;
      })
      .addCase(fetchMonthlyTrends.fulfilled, (state, action) => {
        state.isLoadingMonthlyTrends = false;
        state.monthlyTrends = action.payload;
      })
      .addCase(fetchMonthlyTrends.rejected, (state, action) => {
        state.isLoadingMonthlyTrends = false;
        state.monthlyTrendsError = action.payload;
      })

      // Fetch Amount Distribution
      .addCase(fetchAmountDistribution.pending, (state) => {
        state.isLoadingAmountDistribution = true;
        state.amountDistributionError = null;
      })
      .addCase(fetchAmountDistribution.fulfilled, (state, action) => {
        state.isLoadingAmountDistribution = false;
        state.amountDistribution = action.payload;
      })
      .addCase(fetchAmountDistribution.rejected, (state, action) => {
        state.isLoadingAmountDistribution = false;
        state.amountDistributionError = action.payload;
      })

      // Fetch Order Type Breakdown
      .addCase(fetchOrderTypeBreakdown.pending, (state) => {
        state.isLoadingOrderTypeBreakdown = true;
        state.orderTypeBreakdownError = null;
      })
      .addCase(fetchOrderTypeBreakdown.fulfilled, (state, action) => {
        state.isLoadingOrderTypeBreakdown = false;
        state.orderTypeBreakdown = action.payload;
      })
      .addCase(fetchOrderTypeBreakdown.rejected, (state, action) => {
        state.isLoadingOrderTypeBreakdown = false;
        state.orderTypeBreakdownError = action.payload;
      })

      // Fetch Season Performance
      .addCase(fetchSeasonPerformance.pending, (state) => {
        state.isLoadingSeasonPerformance = true;
        state.seasonPerformanceError = null;
      })
      .addCase(fetchSeasonPerformance.fulfilled, (state, action) => {
        state.isLoadingSeasonPerformance = false;
        state.seasonPerformance = action.payload;
      })
      .addCase(fetchSeasonPerformance.rejected, (state, action) => {
        state.isLoadingSeasonPerformance = false;
        state.seasonPerformanceError = action.payload;
      })

      // Fetch Sales by Sales Person
      .addCase(fetchSalesBySalesPerson.pending, (state) => {
        state.isLoadingSalesBySalesPerson = true;
        state.salesBySalesPersonError = null;
      })
      .addCase(fetchSalesBySalesPerson.fulfilled, (state, action) => {
        state.isLoadingSalesBySalesPerson = false;
        state.salesBySalesPerson = action.payload;
      })
      .addCase(fetchSalesBySalesPerson.rejected, (state, action) => {
        state.isLoadingSalesBySalesPerson = false;
        state.salesBySalesPersonError = action.payload;
      })

      // Fetch Sales by Currency
      .addCase(fetchSalesByCurrency.pending, (state) => {
        state.isLoadingSalesByCurrency = true;
        state.salesByCurrencyError = null;
      })
      .addCase(fetchSalesByCurrency.fulfilled, (state, action) => {
        state.isLoadingSalesByCurrency = false;
        state.salesByCurrency = action.payload;
      })
      .addCase(fetchSalesByCurrency.rejected, (state, action) => {
        state.isLoadingSalesByCurrency = false;
        state.salesByCurrencyError = action.payload;
      });
  },
});

export const { clearAnalyticsErrors, resetAnalytics } = salesAnalyticsSlice.actions;
export default salesAnalyticsSlice.reducer;
